import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../../api/apiConfig';
import { Button, Input, Select, FormField, Alert, Spinner, Textarea, PersonAutocomplete } from '../ui';
import { Project } from '../../types/project';
import { Business } from '../../types/business';
import { COLORS, SPACING, TYPOGRAPHY } from '../../design/tokens';

interface Expectation {
  id: number;
  description: string;
  from_text?: string;
  persons?: { id: number; name: string }[];
  expected_by?: string | null;
  project_id?: number;
  project_detail?: { id: number; name: string } | null;
  business_id?: number;
  business_detail?: { id: number; name: string } | null;
  email_id?: number;
  notes?: string;
  status: string;
}

interface ExpectationFormProps {
  isOpen: boolean;
  onClose: () => void;
  onExpectationCreated: () => void;
  onExpectationUpdated?: (updatedExpectation: Expectation) => void;
  initialExpectationData?: Expectation | null;
  isEditMode?: boolean;
  // Pre-fill data from email context
  prefillData?: {
    email_id?: number;
    person_ids?: number[];
    project_id?: number;
    business_id?: number;
  };
}

interface ExpectationFormData {
  description: string;
  from_text: string;
  person_ids: number[];
  expected_by: string;
  project_id: string;
  business_id: string;
  email_id: number | null;
  notes: string;
}

const ExpectationForm: React.FC<ExpectationFormProps> = ({
  isOpen,
  onClose,
  onExpectationCreated,
  onExpectationUpdated,
  initialExpectationData = null,
  isEditMode = false,
  prefillData,
}) => {
  const [formData, setFormData] = useState<ExpectationFormData>({
    description: '',
    from_text: '',
    person_ids: [],
    expected_by: '',
    project_id: '',
    business_id: '',
    email_id: null,
    notes: '',
  });

  const [projects, setProjects] = useState<Project[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen) return;
      setIsLoadingData(true);
      setFetchError(null);
      try {
        const [projRes, bizRes] = await Promise.all([
          apiClient.get('/api/projects/'),
          apiClient.get('/api/businesses/'),
        ]);
        setProjects(projRes.data || []);
        setBusinesses(bizRes.data || []);
      } catch (err) {
        console.error("Error fetching form data:", err);
        setFetchError("Failed to load form data. Please try again.");
      } finally {
        setIsLoadingData(false);
      }
    };
    fetchData();
  }, [isOpen]);

  useEffect(() => {
    if (isEditMode && initialExpectationData && isOpen) {
      setFormData({
        description: initialExpectationData.description || '',
        from_text: initialExpectationData.from_text || '',
        person_ids: initialExpectationData.persons?.map(p => p.id) || [],
        expected_by: initialExpectationData.expected_by || '',
        project_id: initialExpectationData.project_detail?.id?.toString() || '',
        business_id: initialExpectationData.business_detail?.id?.toString() || '',
        email_id: initialExpectationData.email_id || null,
        notes: initialExpectationData.notes || '',
      });
    } else if (!isEditMode && isOpen) {
      // Reset form and apply prefill data
      setFormData({
        description: '',
        from_text: '',
        person_ids: prefillData?.person_ids || [],
        expected_by: '',
        project_id: prefillData?.project_id?.toString() || '',
        business_id: prefillData?.business_id?.toString() || '',
        email_id: prefillData?.email_id || null,
        notes: '',
      });
    }
  }, [isEditMode, initialExpectationData, isOpen, prefillData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(false);

    const payload = {
      description: formData.description,
      from_text: formData.from_text,
      person_ids: formData.person_ids,
      expected_by: formData.expected_by || null,
      project_id: formData.project_id ? parseInt(formData.project_id) : null,
      business_id: formData.business_id ? parseInt(formData.business_id) : null,
      email_id: formData.email_id,
      notes: formData.notes,
    };

    try {
      let response;
      if (isEditMode && initialExpectationData?.id) {
        response = await apiClient.patch(`/api/expectations/${initialExpectationData.id}/`, payload);
      } else {
        response = await apiClient.post('/api/expectations/', payload);
      }

      const responseData = response.data;
      setSubmitSuccess(true);
      if (isEditMode && onExpectationUpdated) onExpectationUpdated(responseData);
      else onExpectationCreated();
      setTimeout(() => onClose(), 1500);
    } catch (err: unknown) {
      let errorMessage = `Failed to ${isEditMode ? 'update' : 'create'} expectation. Please try again.`;

      if (err instanceof Error) {
        errorMessage = err.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = useCallback(() => {
    if (!isSubmitting) onClose();
  }, [isSubmitting, onClose]);

  const [closeButtonHovered, setCloseButtonHovered] = useState(false);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" style={{ position: 'relative', zIndex: 50 }} onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
          }} />
        </Transition.Child>

        <div style={{
          position: 'fixed',
          inset: 0,
          overflowY: 'auto',
        }}>
          <div style={{
            display: 'flex',
            minHeight: '100%',
            alignItems: 'center',
            justifyContent: 'center',
            padding: SPACING[16],
            textAlign: 'center',
          }}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel style={{
                width: '100%',
                maxWidth: '28rem',
                transform: 'none',
                overflow: 'hidden',
                borderRadius: '16px',
                backgroundColor: COLORS.bg.panel,
                padding: SPACING[24],
                textAlign: 'left',
                verticalAlign: 'middle',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                transition: 'all',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: SPACING[16],
                }}>
                  <Dialog.Title as="h3" style={{
                    fontSize: TYPOGRAPHY.size['2xl'],
                    fontWeight: TYPOGRAPHY.weight.medium,
                    color: COLORS.text.primary,
                  }}>
                    {isEditMode ? 'Edit Expectation' : 'Create Expectation'}
                  </Dialog.Title>
                  <button
                    type="button"
                    style={{
                      color: closeButtonHovered ? COLORS.text.secondary : COLORS.text.tertiary,
                      opacity: isSubmitting ? 0.5 : 1,
                      background: 'none',
                      border: 'none',
                      cursor: isSubmitting ? 'not-allowed' : 'pointer',
                      padding: 0,
                    }}
                    onMouseEnter={() => setCloseButtonHovered(true)}
                    onMouseLeave={() => setCloseButtonHovered(false)}
                    onClick={handleClose}
                    disabled={isSubmitting}
                  >
                    <XMarkIcon style={{ height: '24px', width: '24px' }} />
                  </button>
                </div>

                {fetchError && (
                  <Alert variant="error" style={{ marginBottom: SPACING[16] }}>
                    {fetchError}
                  </Alert>
                )}

                {isLoadingData ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '10rem',
                  }}>
                    <Spinner size="md" />
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
                    <FormField label="What are you expecting? *" htmlFor="description">
                      <Input
                        type="text"
                        id="description"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        placeholder="e.g., Signed contract, feedback on proposal..."
                        required
                      />
                    </FormField>

                    <FormField label="From whom?" htmlFor="from_text">
                      <Input
                        type="text"
                        id="from_text"
                        name="from_text"
                        value={formData.from_text}
                        onChange={handleInputChange}
                        placeholder="e.g., Amazon, The bank, John's team..."
                      />
                    </FormField>

                    <PersonAutocomplete
                      selectedPersonIds={formData.person_ids}
                      onChange={(ids) => setFormData(prev => ({ ...prev, person_ids: ids }))}
                      label="Or link to contacts (optional)"
                      placeholder="Type to search for people..."
                    />

                    <FormField label="Expected by (Optional)" htmlFor="expected_by">
                      <Input
                        type="date"
                        id="expected_by"
                        name="expected_by"
                        value={formData.expected_by}
                        onChange={handleInputChange}
                      />
                    </FormField>

                    <FormField label="Project (Optional)" htmlFor="project_id">
                      <Select
                        id="project_id"
                        name="project_id"
                        value={formData.project_id}
                        onChange={handleInputChange}
                      >
                        <option value="">No Project</option>
                        {projects.map((proj) => (
                          <option key={proj.id} value={proj.id}>
                            {proj.name}
                          </option>
                        ))}
                      </Select>
                    </FormField>

                    <FormField label="Business (Optional)" htmlFor="business_id">
                      <Select
                        id="business_id"
                        name="business_id"
                        value={formData.business_id}
                        onChange={handleInputChange}
                      >
                        <option value="">No Business</option>
                        {businesses.map((biz) => (
                          <option key={biz.id} value={biz.id}>
                            {biz.name}
                          </option>
                        ))}
                      </Select>
                    </FormField>

                    <FormField label="Notes (Optional)" htmlFor="notes">
                      <Textarea
                        id="notes"
                        name="notes"
                        value={formData.notes}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Any additional context..."
                      />
                    </FormField>

                    {formData.email_id && (
                      <p style={{
                        fontSize: TYPOGRAPHY.size.sm,
                        color: COLORS.text.secondary,
                        fontStyle: 'italic',
                      }}>
                        Linked to email
                      </p>
                    )}

                    {submitSuccess && (
                      <Alert variant="success">
                        {isEditMode ? 'Expectation updated successfully!' : 'Expectation created successfully!'}
                      </Alert>
                    )}

                    {submitError && (
                      <Alert variant="error">
                        {submitError}
                      </Alert>
                    )}

                    <div style={{
                      marginTop: SPACING[24],
                      display: 'flex',
                      justifyContent: 'flex-end',
                      gap: SPACING[12],
                    }}>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleClose}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        isLoading={isSubmitting}
                        loadingText={isEditMode ? 'Updating...' : 'Creating...'}
                        disabled={isLoadingData || fetchError !== null}
                      >
                        {isEditMode ? 'Update Expectation' : 'Create Expectation'}
                      </Button>
                    </div>
                  </form>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ExpectationForm;
